'use client';

import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LEAGUE_LABELS } from '@pickleballinc/lib/product-labels';
import { Session } from '@pickleballinc/lib/session';
import { useAppConfig } from '@pickleballinc/ui/components/global/ApplicationConfigProvider';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@pickleballinc/ui/components/ui/dropdown-menu';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';

import { useImpersonationBarHeight } from '@/hooks/useImpersonationBarHeight';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';
import { IS_PRODUCTION } from '@/utils/helpers/constants';
import { getWithBaseUrl } from '@/utils/helpers/getWithBaseUrl';

interface NavigationTabsProps {
  isProPlayer: boolean | undefined;
  isUserAlsoPlayer: boolean;
  playerSlug: string;
  user?: Session;
  proLevel?: number;
  /** When false (i.e. WPR is active), hide the DUPR "Rating History" tab entirely. */
  isDuprActive: boolean;
}

export function NavigationTabs({
  isProPlayer,
  isUserAlsoPlayer,
  playerSlug,
  user,
  proLevel,
  isDuprActive,
}: NavigationTabsProps) {
  const pathName = usePathname();

  const { hasPermission } = useAuth();
  const isSuperAdmin = hasPermission(Permission.SUPER_ADMIN);

  const playerPath = pathName?.split('/') ?? [];
  const activeTab = playerPath[3] ?? '';

  const disableTab = !user?.token && Number(proLevel) !== 5;

  const {
    config: { isImpersonationInProcess },
  } = useAppConfig();

  const barHeight = useImpersonationBarHeight();

  let navItems = [
    {
      name: 'Overview',
      href: '',
      tabIndex: 0,
    },
    {
      name: 'Stats',
      href: `stats?active_filter=doubles&show=${isProPlayer ? 'ppa' : 'overall'}`,
      tabIndex: 1,
    },
    {
      name: 'Tournaments',
      href: 'tournaments?upcoming=true&tournament_type=registeredfor',
      tabIndex: 2,
    },
    {
      name: LEAGUE_LABELS.plural,
      href: 'league?league_type=registeredfor&status=current',
      tabIndex: 3,
    },
    {
      name: 'Team Leagues',
      href: 'team-leagues?status=future&team_league_type=registeredfor',
      tabIndex: 4,
    },
    {
      name: 'Clubs',
      href: 'clubs?club_type=memberof',
      tabIndex: 5,
    },
    {
      name: 'Videos',
      href: 'videos?tournament=all',
      tabIndex: 6,
    },
  ];

  if (isProPlayer) {
    navItems.push({
      name: 'Sponsors',
      href: 'sponsors',
      tabIndex: 7,
    });
  }

  navItems.push({
    name: 'Gear',
    href: 'my-gear',
    tabIndex: 12,
  });

  if (!isProPlayer) {
    navItems.push({
      name: 'Achievements',
      href: 'achievements',
      tabIndex: 11,
    });
  }

  if (disableTab) {
    navItems = navItems.filter((item) => item.name !== 'Stats' && item.name !== 'Ratings');
  }
  if (isUserAlsoPlayer || isSuperAdmin) {
    navItems.push({ name: 'My News', href: 'my-news', tabIndex: 8 });
  }

  if (isSuperAdmin) {
    navItems.push({ name: 'Ratings', href: 'ratings', tabIndex: 9 });
  }

  // DUPR-only tab. Hidden when WPR is the active system rating.
  if (isDuprActive) {
    navItems.push({ name: 'Rating History', href: 'rating-history', tabIndex: 10 });
  }

  const isRatingsOldActive = pathName?.includes('ratings-old');

  if (!IS_PRODUCTION && !isProPlayer && !disableTab && isRatingsOldActive && isSuperAdmin) {
    navItems.push({ name: 'Ratings Old', href: 'ratings-old', tabIndex: 10 });
  }

  const formatTabName = (tab: string) => {
    if (tab === '') {
      return 'Overview';
    }
    return tab
      .split(/[-]/)
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <>
      <div
        className='w-full px-4 py-6 sm:hidden flex sticky z-40 bg-gray-50'
        style={isImpersonationInProcess ? { top: `${barHeight}px` } : { top: '-5px' }}
      >
        <DropdownMenu>
          <DropdownMenuTrigger className='outline-none w-full'>
            <div className='relative flex items-center justify-between px-[14px] py-[10px] text-base text-gray-600 bg-white font-medium rounded-md shadow-xs'>
              {formatTabName(activeTab)}
              <FontAwesomeIcon icon={faChevronDown} size='sm' />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent className='flex flex-col bg-white w-[--radix-dropdown-menu-trigger-width]'>
            {navItems
              .filter(({ href }) => href.split('?')[0] !== activeTab)
              .map((item) => {
                return (
                  <DropdownMenuItem asChild key={`mobile-navigation-item-${item.tabIndex}`}>
                    <CustomLink
                      className='inline-flex w-full whitespace-nowrap !text-base !text-gray-600 !font-medium'
                      href={getWithBaseUrl(`/players/${playerSlug}/${item.href}`)}
                      data-testid={`players-nav-${item.name}`}
                    >
                      {item.name}
                    </CustomLink>
                  </DropdownMenuItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div
        style={isImpersonationInProcess ? { top: `${barHeight}px` } : { top: '0' }}
        className='hidden sm:flex sticky z-40 mb-8 w-full bg-purple-50 border border-purple-100'
      >
        <div className='mx-auto container px-[14px] xl:px-0'>
          <div className='flex invisible-scrollbar overflow-x-scroll xl:overflow-visible justify-center'>
            <ul className='flex items-center py-[6px] w-full sm:max-w-[1240px]'>
              {navItems.map((item) => {
                const isActive =
                  item.href === '' ? activeTab === '' : activeTab === item.href.split('?')[0];
                return (
                  <CustomLink
                    className={clsx(
                      'inline-flex w-full justify-center cursor-pointer whitespace-nowrap px-[14px] p-[10px] text-base font-semibold hover:text-opacity-60 rounded-md',
                      isActive ? 'bg-purple-600 text-white' : 'bg-transparent text-gray-700'
                    )}
                    href={getWithBaseUrl(`/players/${playerSlug}/${item.href}`)}
                    key={`desktop-navigation-item-${item.tabIndex}`}
                    data-testid={`players-nav-${item.name}`}
                  >
                    <li className='list-none'>{item.name}</li>
                  </CustomLink>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}
